import React, { useContext } from 'react';
import { PapiTopic } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import PageContainer from '../../_partials/PageContainer';
import ContentLoader from '../../../ContentLoader';
import BrowseThumbLayout from '../../../BrowseThumbLayout';
import TopicHeader from './TopicHeader';
import RelatedTopics from './RelatedTopics';

import s from './TopicPage.scss';
const relatedTopicsMobileHeight =
  parseInt(s['related-topics-mobile-height'], 10) || 86;
const relatedTopicsMarginTop =
  parseInt(s['related-topics-margin-top'], 10) || 16;
const relatedTopicsHeight = relatedTopicsMobileHeight + relatedTopicsMarginTop;
const RELATED_TOPICS_MOBILE_INDEX_IN_DEVIATIONS = 6;

const TOPIC_RESOURCE_TYPE = 78;

export interface Props {
  /* the single topic being showcased/explored */
  topic?: PapiTopic;
  /* an array of topics related to this one */
  relatedTopics?: PapiTopic[];
  /* estimated number of deviations for this topic */
  estTotal?: number;
  initialOffset?: number;
}

export const TopicPage: React.FC<Props> = props => {
  const {
    topic = {} as PapiTopic,
    relatedTopics = [],
    estTotal,
    initialOffset,
  } = props;
  const isMobile = useContext(MobileContext);

  const { name, description } = topic;
  if (!name || !description) {
    return null;
  }

  return (
    <PageContainer
      withNavigation={false}
      withTabHeader={false}
      withNetworkBar={false}
    >
      <ContentLoader>
        <article className={s['root']}>
          <BiLoggerContextProvider
            value={{
              section_typeid: TOPIC_RESOURCE_TYPE,
              sectionid: topic.itemId,
            }}
          >
            <TopicHeader
              topic={topic}
              relatedTopics={relatedTopics}
              estTotal={estTotal}
            />

            <BiLoggerContextProvider
              value={{ sectionname: 'general', _rankOffset: initialOffset }}
            >
              <div className={s['deviations']}>
                {isMobile ? (
                  <BrowseThumbLayout
                    {...relatedTopicsAsContentStrip(relatedTopics)}
                  />
                ) : (
                  <BrowseThumbLayout />
                )}
              </div>
            </BiLoggerContextProvider>
          </BiLoggerContextProvider>
        </article>
      </ContentLoader>
    </PageContainer>
  );
};
TopicPage.displayName = 'TopicPage';
export default TopicPage;

/** Design has the related topics list mixed into the deviations
 *  but only when on mobile, so we hide it in the topic header and
 *  send it into the BrowseThumbLayout like it was a curated strip */
function relatedTopicsAsContentStrip(relatedTopics: PapiTopic[] = []):
  | {
      contentStrip: React.ReactNode;
      contentStripIndex: number;
      contentStripHeight: number;
    }
  | {} {
  return relatedTopics!.length
    ? {
        contentStrip: (
          <RelatedTopics
            key="related-topics"
            className={s['mobile-related-topics']}
            topics={relatedTopics}
          />
        ),
        contentStripHeight: relatedTopicsHeight,
        contentStripIndex: RELATED_TOPICS_MOBILE_INDEX_IN_DEVIATIONS,
      }
    : {};
}
